import { Color } from '@coast/schemas';
import { isEmpty, isNil, uniqBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { CreateTagRow, Tag } from '@shared/components/Tag';
import { View } from '@shared/components/View';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelectModal } from '@shared/components/multiSelect';
import { Pill } from '@shared/components/pill';
import { TagField } from '@shared/data/entity/fields/TagField';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { getAnchorName, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { notNil } from '@shared/util/notNil';
import { EditFieldContainer } from '../EditFieldContainer';
import { LookupEditFieldContainerProps } from '../EditFieldContainerProps';
import { useAddTag } from './useAddTag';
import { useUpdateTag } from './useUpdateTag';

interface Props extends LookupEditFieldContainerProps {
  field: TagField | null;
  component: API.TagComponent | API.RelatedCardLookupComponent;
  workflowTemplateId: string;
  updateField?: (componentId: string, field: TagField | null) => void;
  allowCreate?: boolean;
  allowClear?: boolean;
}

export function TagEditField({
  field,
  component,
  updateField,
  workflowTemplateId,
  error,
  allowCreate,
  allowClear,
  showIcon,
  label,
  showLabel = true,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const { addTag } = useAddTag(workflowTemplateId, component.id);
  const { updateTag } = useUpdateTag(workflowTemplateId, component.id);
  const [recentlyCreatedTagOptions, setRecentlyCreatedTagOptions] = useState<
    API.TagComponentOption[]
  >([]);
  const tagOptions = useMemo(
    () => (isTagComponent(component) ? component.options ?? [] : field?.tagOptions ?? []),
    [component, field],
  );
  const isMultiselect = isTagComponent(component)
    ? component.multiselect ?? false
    : field?.multiselect ?? false;
  const addAdditionalValues = isTagComponent(component)
    ? component.addAdditionalValues ?? false
    : field?.addAdditionalValues ?? false;
  const componentOptions = useMemo(() => {
    return uniqBy([...recentlyCreatedTagOptions, ...tagOptions], t => t.value);
  }, [tagOptions, recentlyCreatedTagOptions]);
  const options = useMemo(
    () => componentOptions.filter(option => !option.archived) ?? [],
    [componentOptions],
  );
  const archived = useMemo(
    () => componentOptions.filter(option => !!option.archived) ?? [],
    [componentOptions],
  );
  const optionValues = useMemo(
    () => componentOptions.map(option => option.value),
    [componentOptions],
  );
  const archivedValues = useMemo(() => archived.map(option => option.value), [archived]);

  const labelExtractor = useCallback(
    (value: string) => componentOptions.find(option => option.value === value)?.label ?? '',
    [componentOptions],
  );

  const selectedTags = useMemo<API.TagComponentOption[]>(() => {
    const selected = field?.tagIds ?? [];
    return selected
      .map((id: string) => componentOptions.find(item => item.value === id))
      .filter(notNil);
  }, [field?.tagIds, componentOptions]);

  const onChange = useCallback(
    (selection: string[]) => {
      updateField?.(
        component.id,
        !isNil(selection) && !isEmpty(selection)
          ? new TagField(selection, component.id, tagOptions, isMultiselect, addAdditionalValues)
          : null,
      );
    },
    [component.id, tagOptions, isMultiselect, addAdditionalValues, updateField],
  );

  const renderItem = useCallback(
    (value: string) => {
      const option = componentOptions.find(item => item.value === value);
      return <SelectModalItem option={option} />;
    },
    [componentOptions],
  );

  const createAndSelectNew = useCallback(
    async (value: string) => {
      try {
        const result = await addTag(value);
        if (!isNil(result)) {
          const newSelectedTags = isMultiselect
            ? [...selectedTags.map(tag => tag.value), result.value]
            : [result.value];
          setRecentlyCreatedTagOptions([...recentlyCreatedTagOptions, result]);
          updateField?.(
            component.id,
            new TagField(
              newSelectedTags,
              component.id,
              [...tagOptions, result],
              isMultiselect,
              addAdditionalValues,
            ),
          );
        }
      } catch (err) {
        // don't care
      }
    },
    [
      addTag,
      component.id,
      isMultiselect,
      tagOptions,
      recentlyCreatedTagOptions,
      selectedTags,
      updateField,
      addAdditionalValues,
    ],
  );

  const match = useCallback(
    (value: string, query: string, exact: boolean) => {
      const labelToMatch = labelExtractor(value);
      if (isNil(labelToMatch)) {
        return false;
      }
      if (exact) {
        return labelToMatch.toLowerCase() === query?.toLowerCase();
      }
      return labelToMatch.toLowerCase().includes(query?.toLowerCase());
    },
    [labelExtractor],
  );

  const renderCreateNew = useCallback(
    (query: string, creating: boolean) => {
      if (creating) {
        return (
          <View style={{ height: grid(8) }} center>
            <MBLoadingIndicator />
          </View>
        );
      }

      return <CreateTagRow query={query} numberOfOptions={options.length} />;
    },
    [options.length],
  );

  const onCreate = allowCreate ? createAndSelectNew : undefined;

  const onChangeLabel = useCallback(
    async (value: string, newLabel: string) => {
      updateTag(value, { label: newLabel });
    },
    [updateTag],
  );

  const onArchive = useCallback(
    async (value: string) => {
      await updateTag(value, { archived: true });
      setRecentlyCreatedTagOptions(prev => prev.filter(option => option.value !== value));
    },
    [updateTag],
  );

  const onUnarchive = useCallback(
    async (value: string) => {
      updateTag(value, { archived: false });
    },
    [updateTag],
  );

  const onClear = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const hasTags = selectedTags.length > 0;
  const isReadOnly = !!component.readonly || isNil(updateField);
  const showClearButton = hasTags && !isReadOnly && allowClear;
  const anchorName = getAnchorName('tag-edit-field', component.id);
  const printContent = selectedTags.map(t => t.label).join(', ') || undefined;

  if (isReadOnly) {
    return (
      <TagFieldSummary
        component={component}
        anchorName={anchorName}
        selectedTags={selectedTags}
        hasTags={hasTags}
        isReadOnly={isReadOnly}
        showIcon={showIcon}
        label={label}
        showLabel={showLabel}
        header={header}
        footer={footer}
        subfooter={subfooter}
        isGrayScale={isGrayScale}
        isItalic={isItalic}
        style={style}
        printContent={printContent}
      />
    );
  }

  return (
    <MBMultiSelectModal<string>
      label={component.label ?? ''}
      anchorName={anchorName}
      onChange={onChange}
      onChangeLabel={onChangeLabel}
      onArchive={onArchive}
      onUnarchive={onUnarchive}
      onCreate={onCreate}
      selectAll
      selectAllLabel={i18n('card.blocks.tagsInput.selectAllLabel')}
      renderItem={renderItem}
      renderArchivedItem={renderItem}
      values={optionValues}
      selectedValues={selectedTags?.map(t => t.value)}
      labelExtractor={labelExtractor}
      match={match}
      renderCreateNew={renderCreateNew}
      archivedValues={archivedValues}
      itemLabel={i18n('card.blocks.tagsInput.itemLabel')}
      itemLabelPlural={i18n('card.blocks.tagsInput.itemLabelPlural')}
      errorLabel={i18n('errors.tag.enterName')}
      itemsEditable={addAdditionalValues}
      max={isMultiselect ? undefined : 1}
    >
      <TagFieldSummary
        component={component}
        anchorName={anchorName}
        selectedTags={selectedTags}
        hasTags={hasTags}
        onClear={showClearButton ? onClear : undefined}
        error={error}
        isReadOnly={isReadOnly}
        showIcon={showIcon}
        label={label}
        showLabel={showLabel}
        header={header}
        footer={footer}
        subfooter={subfooter}
        isGrayScale={isGrayScale}
        isItalic={isItalic}
        style={style}
        printContent={printContent}
      />
    </MBMultiSelectModal>
  );
}

interface TagFieldSummaryProps extends LookupEditFieldContainerProps {
  component: API.TagComponent | API.RelatedCardLookupComponent;
  anchorName: string;
  selectedTags: API.TagComponentOption[];
  onClear?: () => void;
  hasTags: boolean;
  error?: string;
  isReadOnly: boolean;
  printContent?: string;
}

function TagFieldSummary({
  component,
  anchorName,
  selectedTags,
  onClear,
  hasTags,
  error,
  isReadOnly,
  showIcon,
  label,
  showLabel,
  header,
  footer,
  subfooter,
  isGrayScale,
  isItalic,
  style,
  printContent,
}: TagFieldSummaryProps) {
  const showClearButton = hasTags && !isReadOnly;

  return (
    <EditFieldContainer
      component={component}
      anchorName={anchorName}
      showIcon={showIcon}
      label={label}
      showLabel={showLabel}
      showPlaceholder={!hasTags}
      showClearButton={showClearButton}
      onClear={onClear}
      error={error}
      header={header}
      footer={footer}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      isReadOnly={isReadOnly}
      style={style}
      printContent={printContent}
    >
      {hasTags && (
        <View row wrap paddingVertical={0.5} paddingRight={1} marginLeft={-1}>
          {selectedTags.map(tag => (
            <Tag
              key={tag.value}
              label={tag.label}
              color={tag.backgroundColor as Color}
              archived={!!tag.archived}
              style={styles.pill}
            />
          ))}
        </View>
      )}
    </EditFieldContainer>
  );
}

function SelectModalItem({ option }: { option?: API.TagComponentOption }) {
  if (isNil(option)) {
    return null;
  }
  return (
    <View row centerVertically flex>
      <Pill
        key={option?.value}
        backgroundColor={
          option?.archived ? 'backgroundGrayLight' : (option?.backgroundColor as Color)
        }
        round
        italic={option?.archived ?? false}
        style={styles.pill}
      >
        {option?.label}
      </Pill>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    marginLeft: grid(0.5),
  },
});
